import { memo, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import { ptBR } from 'date-fns/locale';
import { Socket } from 'socket.io-client';
import {
  FiMoreVertical,
  FiTrash2,
  FiSlash,
  FiStar,
  FiUnlock,
  FiMapPin,
  FiCornerUpLeft,
} from 'react-icons/fi';
import parseISO from 'date-fns/parseISO';
import Tippy from '@tippyjs/react';
import toast from 'react-hot-toast';

import { ReceivedChatMessage } from '@common/types/chat-socket';
import { Avatar, Button } from '@components';
import { selectUser } from '@hooks/useAuth/selectors';
import { useAuth } from '@hooks';
import { api } from '@services';

import * as S from './styles';

type ReplyData = {
  author_name: string;
  content: string;
};

type MessageProps = ReceivedChatMessage & {
  groupId?: number;
  groupKey?: string;
  socket?: Socket;
  pinnedMessageId?: number;
  onPin?: (message: ReceivedChatMessage) => void;
  onReply?: (data: ReplyData) => void;
};

const Message = ({
  groupId,
  groupKey,
  socket,
  pinnedMessageId,
  onPin,
  onReply,
  ...data
}: MessageProps) => {
  const user = useAuth(selectUser);
  const { t } = useTranslation();

  const {
    id,
    author_id,
    author_avatar,
    author_name,
    content,
    createdAt,
    reply_to_author,
    reply_to_content,
    moderator: isMessageUserAModerator,
  } = data;

  const tippyRef = useRef<{ hide: () => void } | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const isCurrentUserAModerador = !!user.moderator;
  const isCurrentUser = Number(author_id) === user.id;
  const isPinned = pinnedMessageId === id;

  const hasMessageBeenDeleted =
    content.toLowerCase() === t('pages.broadcast.chat.messageRemoved');

  const handleDeleteMessage = () => {
    socket?.emit('remove.message', {
      group_id: groupId,
      group_key: groupKey,
      message_id: id,
    });
  };

  const handleBlockUser = async () => {
    try {
      const { data: res } = await api.put(`/user/${author_id}/blockChat`);
      toast.success(res.message);
      setIsBlocked((prev) => !prev);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || 'Erro ao bloquear usuário');
    }
  };

  const handlePinMessage = () => {
    onPin?.(data as ReceivedChatMessage);
  };

  const handleReply = () => {
    onReply?.({ author_name, content });
  };

  const at = t('pages.broadcast.chat.at');
  const formattedDate = useMemo(
    () =>
      format(parseISO(createdAt), `'${at} 'HH'h'mm`, {
        locale: ptBR,
      }),
    [createdAt, at],
  );

  return (
    <S.Container
      $currentUser={isCurrentUser}
      $removedMessage={hasMessageBeenDeleted}
    >
      <Avatar src={author_avatar} alt={author_name} />

      {!hasMessageBeenDeleted && !isCurrentUser && (
        <S.ReplyButtonWrapper
          type="button"
          onClick={handleReply}
          title="Responder"
        >
          <FiCornerUpLeft size={13} />
        </S.ReplyButtonWrapper>
      )}

      <S.MessageContent $currentUser={isCurrentUser}>
        <S.MessageInfo>
          <S.MessageHeader $currentUser={isCurrentUser}>
            <div>
              <h4>
                {author_name}
                <time>{formattedDate}</time>
              </h4>

              {isMessageUserAModerator && (
                <span>
                  <FiStar size={8} fill="currentColor" />{' '}
                  {t('pages.broadcast.chat.moderator')}
                </span>
              )}
            </div>

            {isCurrentUserAModerador && !hasMessageBeenDeleted && (
              <S.ModeratorOptionsContainer $currentUser={isCurrentUser}>
                <Tippy
                  trigger="click"
                  inertia
                  hideOnClick
                  interactive
                  theme="light-border"
                  animation="shift-away-subtle"
                  zIndex={9999}
                  placement="bottom-start"
                  interactiveBorder={12}
                  appendTo={() => document.body}
                  onCreate={(instance) => {
                    tippyRef.current = instance;
                  }}
                  content={
                    <>
                      <S.ModeratorOptionsButton
                        type="button"
                        onClick={() => {
                          handleDeleteMessage();
                          tippyRef.current?.hide();
                        }}
                      >
                        <FiTrash2 size={13} />
                        Remover Mensagem
                      </S.ModeratorOptionsButton>
                      <S.ModeratorOptionsButton
                        type="button"
                        onClick={() => {
                          handleBlockUser();
                          tippyRef.current?.hide();
                        }}
                      >
                        {isBlocked ? (
                          <FiUnlock size={13} />
                        ) : (
                          <FiSlash size={13} />
                        )}
                        {isBlocked ? 'Desbloquear do Chat' : 'Bloquear do Chat'}
                      </S.ModeratorOptionsButton>
                      {!isPinned && (
                        <S.ModeratorOptionsButton
                          type="button"
                          onClick={() => {
                            handlePinMessage();
                            tippyRef.current?.hide();
                          }}
                        >
                          <FiMapPin size={13} />
                          Fixar Mensagem
                        </S.ModeratorOptionsButton>
                      )}
                    </>
                  }
                >
                  <Button
                    size="even"
                    variant="transparent"
                    icon={FiMoreVertical}
                  />
                </Tippy>
              </S.ModeratorOptionsContainer>
            )}
          </S.MessageHeader>
        </S.MessageInfo>

        {reply_to_author && reply_to_content && (
          <S.ReplyQuote>
            <S.ReplyQuoteIcon>
              <FiCornerUpLeft />
            </S.ReplyQuoteIcon>
            <S.ReplyQuoteBody>
              <S.ReplyQuoteAuthor>{reply_to_author}:</S.ReplyQuoteAuthor>
              <S.ReplyQuoteText> {reply_to_content}</S.ReplyQuoteText>
            </S.ReplyQuoteBody>
          </S.ReplyQuote>
        )}

        <p>{content}</p>
      </S.MessageContent>
    </S.Container>
  );
};

export default memo(Message);
